import { AnimatePresence, motion } from "framer-motion";
import { useCart, PRODUCT, inr } from "@/context/CartContext";

const slug = (k) => k.toLowerCase().replace(/[|\s]+/g, "-");

export default function CartDrawer() {
  const { items, count, total, drawerOpen, setDrawerOpen, setQty, remove, setCheckoutOpen, rebelLine, setRebelLine } =
    useCart();

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-paper text-ink flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b-2 border-black">
              <p className="font-display text-xl tracking-wide">YOUR CART ({count})</p>
              <button
                data-testid="cart-close-button"
                onClick={() => setDrawerOpen(false)}
                className="font-sans font-bold text-sm tracking-[0.2em] hover:text-pink transition-colors"
              >
                CLOSE ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-lenis-prevent>
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4">
                  <p className="font-hand text-3xl text-black/60 -rotate-2">your cart is quiet. too quiet.</p>
                  <button
                    data-testid="cart-empty-cta"
                    onClick={() => setDrawerOpen(false)}
                    className="font-sans font-bold text-xs tracking-[0.25em] border-2 border-black px-6 py-3 hover:bg-black hover:text-paper transition-colors"
                  >
                    GO LOUD →
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((i) => (
                    <li key={i.key} className="flex gap-4 border-b border-black/15 pb-6" data-testid={`cart-item-${slug(i.key)}`}>
                      <img
                        src={i.color === "OFF WHITE" ? "/images/offwhite-front.webp" : PRODUCT.thumb}
                        alt="SHUTUP T-shirt"
                        className="w-20 h-24 object-cover object-top border border-black/20"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between gap-2">
                          <p className="font-sans font-bold text-sm tracking-wide">{PRODUCT.name}</p>
                          <button
                            data-testid={`cart-remove-${slug(i.key)}`}
                            onClick={() => remove(i.key)}
                            className="font-sans text-black/40 text-xs hover:text-blood transition-colors"
                          >
                            REMOVE
                          </button>
                        </div>
                        <p className="font-sans text-black/60 text-xs tracking-[0.2em] mt-1">
                          {i.color} · SIZE — {i.size}
                        </p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-black">
                            <button
                              data-testid={`cart-qty-minus-${slug(i.key)}`}
                              onClick={() => setQty(i.key, i.qty - 1)}
                              className="px-3 py-1 font-bold hover:bg-black hover:text-paper transition-colors"
                            >
                              −
                            </button>
                            <span className="px-3 font-bold text-sm">{i.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${slug(i.key)}`}
                              onClick={() => setQty(i.key, i.qty + 1)}
                              className="px-3 py-1 font-bold hover:bg-black hover:text-paper transition-colors"
                            >
                              +
                            </button>
                          </div>
                          <p className="font-sans font-bold">{inr(PRODUCT.price * i.qty)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t-2 border-black px-6 py-6">
                <div className="flex justify-between font-sans font-bold text-lg mb-1">
                  <span>SUBTOTAL</span>
                  <span data-testid="cart-subtotal">{inr(total)}</span>
                </div>
                <p className="font-sans text-black/50 text-[11px] tracking-[0.2em] mb-4">
                  FREE SHIPPING ACROSS INDIA · SHIPS IN 3–5 DAYS
                </p>
                <label className="block mb-4">
                  <span className="font-sans text-[10px] font-bold tracking-[0.25em] text-black/60">
                    YOUR REBEL LINE (OPTIONAL)
                  </span>
                  <input
                    value={rebelLine}
                    onChange={(e) => setRebelLine(e.target.value)}
                    maxLength={60}
                    data-testid="cart-rebel-line"
                    placeholder="tell them what you never said…"
                    className="mt-1.5 w-full border-2 border-black bg-transparent px-4 py-3 font-hand text-xl outline-none focus:bg-acid/40 placeholder:text-black/30"
                  />
                </label>
                <button
                  data-testid="checkout-button"
                  onClick={() => {
                    setDrawerOpen(false);
                    setCheckoutOpen(true);
                  }}
                  className="w-full bg-black text-paper font-sans font-bold tracking-[0.15em] py-4 hover:bg-pink hover:text-black transition-colors"
                >
                  CHECKOUT →
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
